// Copyright (c) 2022 by Move Closer

import { AnyObject, IResponse, mapModel, ResourceActionFailed } from '@movecloser/front-core'

import {
  AllowedAttributes,
  CartData,
  CartIdentifier,
  CartItemData, CartItemPrice,
  IGraphQL,
  ProductData,
  ProductSneakPeakData
} from '../../../contexts'
import { logger, resolveFromStatus } from '../../../support'

import { IProductsRepository } from '../../products/contracts/repositories'

import { Cart } from '../models/cart'
import { cartAdapterMap } from '../models/cart.adapter'
import { CartModel } from '../contracts/models'
import { CartModuleConfig } from './cart'
import { CheckoutModuleConfig } from './checkout'

/**
 * @author Łukasz Sitnicki <lukasz.sitnicki@movecloser.pl>
 */
export abstract class CartAwareAbstract {
  constructor (
    protected readonly connector: IGraphQL,
    protected readonly products: IProductsRepository,
    protected bundles: Record<string, Array<CartItemData>> | null = null,
    protected regularItems: Array<CartItemData> = []
  ) {
  }

  /**
   * Load missing products' definitions & compose Cart model.
   * @param candidate
   * @param giftFromAttribute (specifies whether the 'isGift' property should be resolved from product attributes
   * @param config (application config for 'cart' interface)
   */
  protected async composeCart (candidate: CartData, giftFromAttribute: boolean = false, config: CartModuleConfig | CheckoutModuleConfig | null = null): Promise<CartModel> {
    const shouldLoadBundles = config && config.hasBundledProducts

    candidate.items = candidate.items.filter(item => item !== null)

    const mappedCandidate: CartData = mapModel(candidate, cartAdapterMap, false)

    if (mappedCandidate.items.length > 0) {
      const items: CartItemData[] = []

      try {
        // Note! This is required because of mapping by merge.
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const skuCollection: string[] = mappedCandidate.items.map(i => i.product.sku)
        const productsCollection = await this.products.loadProductsBySkus(skuCollection)

        const productsMap: Record<string, ProductSneakPeakData> = {}

        for (const product of productsCollection) {
          const variants = Object.values(product.variants)
          const variantsKeys = Object.keys(product.variants)

          for (let i = 0; i < variants.length; i++) {
            for (let j = 0; j < skuCollection.length; j++) {
              if (variants[i].sku === skuCollection[j]) {
                const fVariant = variants[i]
                productsMap[variants[i].sku] = {
                  ...product,
                  variants: { [variantsKeys[i]]: fVariant }
                }
                break
              }
            }
          }
        }

        const resolvedMappedCandidates = await this.getProductsWithResolvedData(mappedCandidate.items)

        for (const i of mappedCandidate.items) {
          let isGift = false
          let isSample = false
          let isPresale = false
          let product

          isGift = this.detectedIfGift(i, giftFromAttribute, resolvedMappedCandidates ?? [])
          isSample = this.detectIfProductHasAttribute(i, AllowedAttributes.IsSample, resolvedMappedCandidates ?? [])
          isPresale = this.detectIfProductHasAttribute(i, AllowedAttributes.IsPresale, resolvedMappedCandidates ?? [])

          if (i.product?.sku && productsMap[i.product.sku]) {
            product = productsMap[i.product.sku]
          } else {
            product = i.product
          }

          items.push({
            ...i,
            isGift: isGift,
            isSample: isSample,
            isPresale: isPresale,
            isInBundle: false,
            sku: i.product?.sku ?? '',
            product
          })
        }

        if (shouldLoadBundles) {
          /**
           * @inheritDoc
           */
          this.loadCartBundles(items)

          mappedCandidate.items = CartAwareAbstract.composeFinalCartItems(this.bundles, this.regularItems)
        } else {
          mappedCandidate.items = items
        }
      } catch (e) {
        console.error(e)
        throw new ResourceActionFailed(
          'Failed to resolve Products',
          resolveFromStatus(500)
        )
      }
    }

    return Cart.hydrate<CartModel>(mappedCandidate)
  }

  /**
   * This is the main bundle composer algorithm. Modify with caution!
   * @param items
   * @private
   */
  private loadCartBundles (items: Array<CartItemData>): void {
    const helperBundles = new Map()
    let duplicateCandidateItems = items

    for (const duplicateItem of duplicateCandidateItems) {
      const variant = duplicateItem.product?.variants ? Object.values(duplicateItem.product.variants)[0] : null
      let variantBundles = variant?.attributes[AllowedAttributes.IsInBundles] as unknown as Record<string, Array<string>>

      // This check will break the bundles loop for non-bundle products
      if (variantBundles) {
        const bundleEntries = Object.entries(variantBundles)
        if (bundleEntries.length > 1) {
          variantBundles = CartAwareAbstract.sortBundlesByMaximumNumberOfChildren(bundleEntries)
        }

        if (variantBundles) {
          // Note: API will return 'parent' sku as e.g. 'parent_100530905' / 'parent_100530905_<something>'
          for (const [parent, children] of Object.entries(variantBundles)) {
            const currentCandidateSkus = duplicateCandidateItems.length > 0 ? duplicateCandidateItems.map(item => item.sku).filter((sku) => children.includes(sku)) : []
            if (currentCandidateSkus.length === 0) {
              break
            }

            const isEachBundleChildInCart = CartAwareAbstract.checkIfAllOtherSiblingsAreInCart(children, currentCandidateSkus)
            let candidateBundles = duplicateCandidateItems.filter(item => currentCandidateSkus.includes(item.sku))
            const minQuantity = CartAwareAbstract.getBundleQuantity(candidateBundles)

            if (isEachBundleChildInCart) {
              candidateBundles = candidateBundles.map((bundle) => {
                return {
                  ...bundle,
                  prices: CartAwareAbstract.getBundleItemPrices(bundle, items, minQuantity),
                  isInBundle: true,
                  bundleSiblings: candidateBundles.filter((b) => b.sku !== bundle.sku).map((candidate) => {
                    return {
                      outOfStock: candidate.outOfStock ?? true,
                      sellableQuantity: candidate.sellableQuantity,
                      parentSku: parent,
                      prices: CartAwareAbstract.getBundleItemPrices(candidate, items, minQuantity),
                      product: candidate.product as ProductData,
                      quantity: minQuantity,
                      uid: candidate.uid
                    }
                  }),
                  quantity: minQuantity
                }
              })

              // Store bundles that have been found and are valid
              helperBundles.set(parent, candidateBundles)

              // Update duplicateCandidateItems quantity
              duplicateCandidateItems = CartAwareAbstract.updateRemainingItemsQuantity(
                duplicateCandidateItems,
                candidateBundles,
                minQuantity
              )
            }
          }
        }
      }
    }

    this.bundles = Object.fromEntries([...helperBundles.entries()])
    this.regularItems = duplicateCandidateItems
  }

  /**
   * Informs whether current cartItem has other bundle siblings that are also present in cart
   * @param childrenSkus
   * @param currentCandidateSkus
   * @private
   */
  private static checkIfAllOtherSiblingsAreInCart (childrenSkus: Array<string>, currentCandidateSkus: Array<string>): boolean {
    return childrenSkus.every((childSku: string) => currentCandidateSkus.includes(childSku))
  }

  /**
   * Decides how to compose final cartItems taking into consideration found bundles and regular cart items
   * @param candidateBundles
   * @param regularItems
   * @private
   */
  private static composeFinalCartItems (
    candidateBundles: Record<string, Array<CartItemData>> | null,
    regularItems: Array<CartItemData>
  ): Array<CartItemData> {
    if (candidateBundles) {
      const finalBundleItems = []
      for (const bundles of Object.values(candidateBundles)) {
        for (const item of bundles) {
          finalBundleItems.push(item)
        }
      }
      return regularItems.concat(finalBundleItems)
    }

    return regularItems
  }

  /**
   * Returns quantity based on bundle children minimum quantity
   * @param candidateBundles
   * @private
   */
  private static getBundleQuantity (candidateBundles: Array<CartItemData>): number {
    return candidateBundles.reduce((first, second) => first.quantity < second.quantity ? first : second).quantity
  }

  /**
   * Returns prices object for each bundle (with proper quantity) based on cartItem prices.
   * @param bundle
   * @param cartItems
   * @param minQuantity
   * @private
   */
  private static getBundleItemPrices (bundle: CartItemData, cartItems: Array<CartItemData>, minQuantity: number): CartItemPrice {
    const foundCartItem = cartItems.find((item) => item.uid === bundle.uid)
    return {
      ...bundle.prices,
      total: {
        ...bundle.prices.total,
        value: foundCartItem ? (foundCartItem.prices.total.value / foundCartItem.quantity) * minQuantity : bundle.prices.total.value
      }
    }
  }

  /**
   *
   * @param bundleEntries e.g. [['parent_100796978', Array(2)], ['parent_100530905', Array(3)]]
   * @private
   */
  private static sortBundlesByMaximumNumberOfChildren (bundleEntries: Array<Array<(string | string[])>>): Record<string, Array<string>> {
    const sortedBundleEntries = bundleEntries.sort((a, b) => b[1].length - a[1].length)
    return Object.fromEntries(sortedBundleEntries as Array<Array<(string | string[])>>)
  }

  /**
   * Updates quantity of cart items that remained to be checked for bundles.
   * If some bundles were found, it's quantity should be subtracted from remaining cart items list.
   * @param duplicateCandidateItems
   * @param candidateBundles
   * @param updatedQuantity
   * @private
   */
  private static updateRemainingItemsQuantity (
    duplicateCandidateItems: Array<CartItemData>,
    candidateBundles: Array<CartItemData>,
    updatedQuantity: number
  ): Array<CartItemData> {
    return duplicateCandidateItems.map((candidate) => {
      if (candidateBundles.map((bundle) => bundle.sku).includes(candidate.sku)) {
        return {
          ...candidate,
          bundleSiblings: undefined,
          isInBundle: false,
          quantity: candidate.quantity - updatedQuantity
        }
      } else {
        return { ...candidate }
      }
    }).filter(item => !(item.quantity <= 0))
  }

  /**
   * Retrieves products data for each cart item.
   * @param mappedCandidateItems
   * @protected
   */
  protected async getProductsWithResolvedData (mappedCandidateItems: Array<CartItemData>): Promise<Array<ProductData>> {
    try {
      const skus = mappedCandidateItems.map((item) => item.product?.sku as string)
      const products = await this.products.loadProductsBySkus(skus)

      if (!products) {
        return []
      }
      return products
    } catch (e) {
      logger(e, 'warn')
      return []
    }
  }

  /**
   * !!!! TODO !!!!
   *
   * This method originally should NOT be a part of abstract class because it applies specific
   * type of resolving isGift property, applicable only for specific case (here Lirene)!
   */
  protected detectedIfGift (item: AnyObject, giftFromAttribute: boolean, resolvedProducts: Array<ProductData>): boolean {
    if (giftFromAttribute) {
      const keywords = ['gratis', 'free']

      const toTest: string = item.product.sku.toLowerCase()

      for (const k of keywords) {
        const shorten: string = toTest.replace(k, '')
        if (shorten.length < toTest.length) {
          return true
        }
      }

      return false
    } else {
      // TODO: Test GIFTS after that optimization change!!!
      const candidateResolvedProduct = resolvedProducts.filter((candidate) =>
        Object.values(candidate.variants).map((variant) => variant.sku).includes(item.product.sku))

      if (candidateResolvedProduct.length === 0) {
        return false
      }

      const candidate = Object.values(candidateResolvedProduct[0].variants).find((product) => product.sku === item.product.sku)

      if (candidate && candidate.attributes[AllowedAttributes.IsGift]) {
        return candidate.attributes[AllowedAttributes.IsGift] as boolean
      }

      return false
    }
  }

  /**
   * !!!! TODO !!!!
   *
   * This method originally should NOT be a part of abstract class because it applies specific
   * type of resolving attribute property
   */
  protected detectIfProductHasAttribute (item: AnyObject, attribute: string, resolvedProducts: Array<ProductData>): boolean {
    // TODO: Test GIFTS and SAMPLES after that optimization change!!!
    const product = resolvedProducts.filter((product) =>
      Object.values(product.variants).map((variant) => variant.sku).includes(item.product.sku))

    if (!product || product.length === 0) {
      return false
    }

    const variants = Object.values(product[0].variants)
    const variant = variants.find((variant) => variant.sku === item.product.sku)
    return variant ? !!variant.attributes[attribute] : false
  }

  /**
   * Loads the cart by its id.
   */
  protected async loadCartById (id: CartIdentifier): Promise<IResponse> {
    return await this.connector.call('cartQuery', { cartId: id })
  }
}
